import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Child, ChildAdapter } from '../models/child.model';
import { FundingRequest, FundingRequestAdapter  } from '../models/funding-request.model';


@Injectable()
export class ChildService {
    private api: string;

    constructor(
        private http: HttpClient,
        private cAdapter: ChildAdapter,
        private frAdapter: FundingRequestAdapter
    ) {
        this.api = `${environment.apiRoot}${environment.apiEndpoints.children}`;
    }

    list(childId?: string): Observable<Child[]> {
        const endpoint = (childId) ? `${this.api}/${childId}` : this.api;
        return this.getChildItems(endpoint).pipe(
            expand(data => data.next ? this.getChildItems(endpoint, data.next.id, data.next.dob) : of()),
            reduce((acc, data) => acc.concat(data.results), [] as Child[]),
        );
    }

    get(childId: string): Observable<Child> {
        const endpoint = `${this.api}/${childId}`;
        return childId ?
            this.http.get<any>(endpoint).pipe(map(response => {
                // Adapt each item in the raw data array to a Child object
                return this.cAdapter.adapt(response.child);
            })) : of();
    }

    getFundingRequests(childId: string): Observable<FundingRequest[]> {
        const endpoint = `${this.api}/${childId}/Applications`;
        return this.getApplicationItems(endpoint).pipe(
            expand(data => data.next ? this.getApplicationItems(endpoint, data.next) : of()),
            reduce((acc, data) => acc.concat(data.results), [] as FundingRequest[]),
        );
    }

    update(childId: string, updateParams: any, version?: number): Observable<Child> {
        // Send everything to the API, as the Lambda function will handle everything
        const endpoint = `${this.api}/${childId}`;
        if (!('version' in updateParams)) {
            if (version) {
                updateParams['version'] = version;
            } else {
                const message = `No version was provided while trying to update: ${childId}`;
                throw new Error(message);
            }
        }
        return childId ?
            this.http.post<any>(endpoint, updateParams).pipe(map(response => {
                // Adapt each item in the raw data array to a Child object
                return this.cAdapter.adapt(response.child);
            })) : of();
    }

    delete(childId: string): Observable<Child> {
        const endpoint = `${this.api}/${childId}`;
        return childId ?
            this.http.delete<any>(endpoint).pipe(map(response => {
                // Adapt each item in the raw data array to a Child object (this returns the deleted data)
                return this.cAdapter.adapt(response.child);
            })) : of();
    }

    private getChildItems(endpoint: string, startId?: string, startDOB?: string): Observable<{next: any, results: Child[]}> {
        if (startId && startDOB) {
            endpoint += `?startId=${startId}&startDOB=${startDOB}`;
        }
        return this.http.get<any>(endpoint).pipe(
            map(response => {
                let nextIds = null;
                if ('lastEvaluatedKey' in response) {
                    nextIds = {
                        id: response.lastEvaluatedKey.PK,
                        dob: response.lastEvaluatedKey.SK
                    };
                }
                return {
                    next: nextIds,
                    results: response.children.map((item: any) => this.cAdapter.adapt(item))
                };
            })
        );
    }

    private getApplicationItems(endpoint: string, startKey?: string): Observable<{next: string, results: FundingRequest[]}> {
        if (startKey) {
            endpoint += `?startKey=${startKey}`;
        }
        return this.http.get<any>(endpoint).pipe(
            map(response => {
                    // TODO rework this for composite key
                    const nextId = ('lastEvaluatedKey' in response) ? response.lastEvaluatedKey.requestId : null;
                return {
                    next: nextId,
                    results: response.applications.map((item: any) => this.frAdapter.adapt(item))
                };
            })
        );
    }
}
